import React, { useEffect, useState } from 'react';
import { Form, message, Tabs } from 'antd';
import {
  registerLegalCustomer,
  registerPhysicalCustomer,
} from '../../../../services/customer/authServices';
import { getLoginFormOptions } from '../../../../services/login/Login.api';
import PhysicalRegistrationFirstStepForm from '../../forms/register/physicalRegistration/PhysicalRegistrationFirstStepForm';
import PhysicalRegistrationSecondStepForm from '../../forms/register/physicalRegistration/PhysicalRegistrationSecondStepForm';
import LegalRegistrationFirstStepForm from '../../forms/register/legalRegistration/LegalRegistrationFirstStepForm';
import LegalRegistrationSecondStepForm from '../../forms/register/legalRegistration/LegalRegistrationSecondStepForm';
import RegisterModal from '../modals/registerModal/RegisterModal';
import RegisterDrawer from '../drawers/registerDrawer/RegisterDrawer';
import { useModal } from '../../../contexts/modalsContext/modalsContext';
import useTranslation from '../../../hooks/translation/useTranslation';
import TermsDrawer from '../drawers/termsDrawer/TermsDrawer';
import TermsModal from '../modals/termsModal/TermsModal';

function Registration() {
  const { trans, configInfo } = useTranslation();
  const { setShowAuthModal, setShowRegisterModal } = useModal();
  const [physicalFormStep, setPhysicalFormStep] = useState(1);
  const [legalFormStep, setLegalFormStep] = useState(1);
  const [registrationType, setRegistrationType] = useState('physical');
  const [formValues, setFormValues] = useState(null);
  const [errors, setErrors] = useState({});
  const [physicalFormFirstStep] = Form.useForm();
  const [legalFormFirstStep] = Form.useForm();
  const [physicalFormSecondStep] = Form.useForm();
  const [legalFormSecondStep] = Form.useForm();

  const handleClose = () => {
    setShowRegisterModal(false);
    physicalFormFirstStep.resetFields();
    legalFormFirstStep.resetFields();
    physicalFormSecondStep.resetFields();
    legalFormSecondStep.resetFields();
    setPhysicalFormStep(1);
    setLegalFormStep(1);
    setErrors({});
  };

  const handleAlreadyRegistered = () => {
    handleClose();
    setTimeout(() => {
      setShowAuthModal(true);
    }, 100);
  };

  const isGeorgian = configInfo?.countryCode === 'GE';

  const handleLegalRegisterSubmit = async () => {
    try {
      const registerValues = {
        ...legalFormFirstStep.getFieldsValue(),
        ...legalFormSecondStep.getFieldsValue(),
        phone_number: legalFormSecondStep.getFieldValue('phone_number').replace(/\s/g, ''),
      };

      if (!isGeorgian) {
        registerValues.verified_email = true;
        registerValues.verified_phone = true;
      }

      const res = await registerLegalCustomer(registerValues);
      if (res.status === 'success') {
        handleClose();
        message.success(trans('you_registered_successfully'));
      }
    } catch (e) {
      const formErrors = e?.response?.data?.errors;
      if (
        formErrors?.country
|| formErrors?.identification_number
|| formErrors?.organization_type_id
|| formErrors?.organization_name
|| formErrors?.bank_account_number
|| formErrors?.legal_address
      ) {
        setLegalFormStep(1);
      } else if (window.innerWidth < 1024) {
        legalFormSecondStep.scrollToField('phone_number', {
          behavior: 'smooth',
        });
      }
      setErrors(e?.response?.data?.errors);
    }
  };

  const handlePhysicalRegisterSubmit = async (values) => {
    try {
      const registerValues = {
        ...physicalFormFirstStep.getFieldsValue(),
        ...physicalFormSecondStep.getFieldsValue(),
        phone_number: values.phone_number.replace(/\s/g, ''),
      };

      if (!isGeorgian) {
        registerValues.verification = 1;
      }

      if (
        formValues.countries.find(
          (country) => country.id === registerValues.citizenship,
        ).name_ka === 'საქართველო'
      ) {
        registerValues.personal_number = registerValues.id_number;
      } else {
        registerValues.passport_number = registerValues.id_number;
      }

      const res = await registerPhysicalCustomer(registerValues);
      if (res.status === 'success') {
        handleClose();
        message.success(trans('you_registered_successfully'));
      }
    } catch (e) {
      const formErrors = e?.response?.data?.errors;
      if (
        formErrors?.first_name
|| formErrors?.last_name
|| formErrors?.personal_number
|| formErrors?.passport_number
|| formErrors?.citizenship
|| formErrors?.gender
      ) {
        setPhysicalFormStep(1);
      } else if (window.innerWidth < 1024) {
        physicalFormSecondStep.scrollToField('email', {
          behavior: 'smooth',
        });
      }
      setErrors(formErrors);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const res = await getLoginFormOptions();
      if (res.status === 'success') {
        setFormValues(res.data);
        legalFormSecondStep.setFieldValue('country_phone_code', '995');
      }
    };
    fetchData();
  }, []);

  const physicalRegistrationItems = [
    {
      key: 1,
      children: (
        <PhysicalRegistrationFirstStepForm
          form={physicalFormFirstStep}
          handleSubmit={() => setPhysicalFormStep(2)}
          formValues={formValues}
          errors={errors}
        />
      ),
    },
    {
      key: 2,
      children: (
        <PhysicalRegistrationSecondStepForm
          formValues={formValues}
          form={physicalFormSecondStep}
          handleSubmit={handlePhysicalRegisterSubmit}
          errors={errors}
          setErrors={setErrors}
          firstForm={physicalFormFirstStep}
        />
      ),
    },
  ];

  const legalRegistrationItems = [
    {
      key: 1,
      children: (
        <LegalRegistrationFirstStepForm
          form={legalFormFirstStep}
          formValues={formValues}
          handleSubmit={() => setLegalFormStep(2)}
          errors={errors}
        />
      ),
    },
    {
      key: 2,
      children: (
        <LegalRegistrationSecondStepForm
          form={legalFormSecondStep}
          formValues={formValues}
          handleSubmit={handleLegalRegisterSubmit}
          errors={errors}
          setErrors={setErrors}
          secondForm={legalFormFirstStep}
        />
      ),
    },
  ];

  const items = [
    {
      key: 'physical',
      label: <h3 className="text-gr-gray text-xl">{trans('physical_person')}</h3>,
      children: (
        <Tabs
          className="no-tablist"
          items={physicalRegistrationItems}
          activeKey={physicalFormStep}
        />
      ),
    },
    {
      key: 'legal',
      label: <h3 className="text-gr-gray text-xl">{trans('legal_person')}</h3>,
      children: (
        <Tabs
          className="no-tablist"
          items={legalRegistrationItems}
          activeKey={legalFormStep}
        />
      ),
    },
  ];
  return (
    <>
      <RegisterModal
        handleAlreadyRegistered={handleAlreadyRegistered}
        items={items}
        legalFormStep={legalFormStep}
        physicalFormStep={physicalFormStep}
        setLegalFormStep={setLegalFormStep}
        registrationType={registrationType}
        setRegistrationType={setRegistrationType}
        handleClose={handleClose}
        setPhysicalFormStep={setPhysicalFormStep}
      />
      <RegisterDrawer
        handleAlreadyRegistered={handleAlreadyRegistered}
        items={items}
        legalFormStep={legalFormStep}
        physicalFormStep={physicalFormStep}
        setLegalFormStep={setLegalFormStep}
        registrationType={registrationType}
        setRegistrationType={setRegistrationType}
        setPhysicalFormStep={setPhysicalFormStep}
        handleClose={handleClose}
      />
      <TermsDrawer />
      <TermsModal />
    </>
  );
}

export default Registration;
